import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import { safePrintHTML } from '../utils/safePrint';
import { writeSanitizedHTML } from '../utils/safePrint';

/**
 * Custom hook for managing ICS 205 Communications Plan state
 * With real-time collaboration support via polling and merge
 * Auto-saves on add, edit, and delete actions
 */
const useCommsPlan = (currentExercise) => {
  const getDefaultFrequency = () => ({
    frequency: '',
    type: '',
    description: '',
    channel: '',
    tone: '',
    offset: '',
    radio_type: '',
    primary_backup: '',
    modulation_type: '',
    zone_grp: '',
    channel_name: '',
    rx_freq: '',
    tx_tone: '',
    remarks: ''
  });

  // ── Field-backup encoding ────────────────────────────────────────────
  // The production backend may run an old Pydantic model that strips the 5 critical fields
  // (zone_grp, channel_name, rx_freq, tx_tone, remarks) from the request payload.
  // Workaround: encode these fields as a JSON backup inside the `description` field
  // (which the old backend DOES preserve). On read, decode and restore.
  const FIELD_BACKUP_MARKER = '|||EX5|||';

  const encodeBackupFields = (freq) => {
    const backup = JSON.stringify({
      zg: freq.zone_grp || '',
      cn: freq.channel_name || '',
      rf: freq.rx_freq || '',
      tt: freq.tx_tone || '',
      rm: freq.remarks || ''
    });
    // Strip any existing backup from description before appending
    const cleanDesc = (freq.description || '').split(FIELD_BACKUP_MARKER)[0];
    return {
      ...freq,
      description: cleanDesc + FIELD_BACKUP_MARKER + backup
    };
  };

  const decodeBackupFields = (freq) => {
    const desc = freq.description || '';
    const markerIdx = desc.indexOf(FIELD_BACKUP_MARKER);
    if (markerIdx === -1) return freq; // No backup encoded
    const cleanDesc = desc.substring(0, markerIdx);
    try {
      const backup = JSON.parse(desc.substring(markerIdx + FIELD_BACKUP_MARKER.length));
      return {
        ...freq,
        description: cleanDesc,
        // Only restore from backup if the native field is empty
        zone_grp: freq.zone_grp || backup.zg || '',
        channel_name: freq.channel_name || backup.cn || '',
        rx_freq: freq.rx_freq || backup.rf || '',
        tx_tone: freq.tx_tone || backup.tt || '',
        remarks: freq.remarks || backup.rm || ''
      };
    } catch (e) {
      return { ...freq, description: cleanDesc };
    }
  };

  // Smart merge: prefer server values BUT never overwrite a non-empty local value with an empty server value.
  // This prevents data loss when the server returns stale/incomplete data for certain fields.
  const smartMergeFreq = (localFreq, serverFreq) => {
    const result = { ...localFreq };
    for (const key of Object.keys(serverFreq)) {
      const serverVal = serverFreq[key];
      const localVal = localFreq[key];
      // Use server value UNLESS it's empty/missing AND local has real data
      if ((serverVal === '' || serverVal === undefined || serverVal === null) && localVal && localVal !== '') {
        continue; // Keep local value — server has no data for this field
      }
      result[key] = serverVal;
    }
    return result;
  };

  const [ics205Frequencies, setIcs205Frequencies] = useState([]);
  const [currentIcs205Frequency, setCurrentIcs205Frequency] = useState(getDefaultFrequency());
  const [editingIcs205Frequency, setEditingIcs205Frequency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [lastServerUpdate, setLastServerUpdate] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [connectionHealth, setConnectionHealth] = useState({
    status: 'checking',  // 'healthy' | 'degraded' | 'error' | 'checking'
    message: 'Checking backend...',
    lastVerifiedAt: null,
    missingFields: [],
    backendVersion: null
  });
  
  // Track local changes for merge
  const localChangesRef = useRef(new Set());
  const deletedIdsRef = useRef(new Set()); // Track deleted frequency IDs
  const pollingIntervalRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const savingRef = useRef(false); // Blocks polling during auto-save to prevent race conditions
  const initialLoadCompleteRef = useRef(false); // Tracks if initial data load finished
  const hasLocalEditsRef = useRef(false); // Guards against stale initial load overwriting local edits
  
  // Ref to always hold the latest currentIcs205Frequency — prevents stale closure issues
  const currentFreqRef = useRef(currentIcs205Frequency);
  currentFreqRef.current = currentIcs205Frequency;

  // Verify backend health: writes a test freq and checks if all 14 fields survive round-trip
  const verifyBackendHealth = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/ics-205/verify`);
      const data = resp.data;
      if (data.critical_fields_ok) {
        setConnectionHealth({
          status: 'healthy',
          message: 'All fields saving correctly',
          lastVerifiedAt: new Date().toISOString(),
          missingFields: [],
          backendVersion: data.backend_version
        });
      } else {
        setConnectionHealth({
          status: 'degraded',
          message: `Backend not saving: ${data.missing_fields.join(', ')}`,
          lastVerifiedAt: new Date().toISOString(),
          missingFields: data.missing_fields,
          backendVersion: data.backend_version || 'unknown'
        });
      }
    } catch (err) {
      // If /verify endpoint doesn't exist (404), the backend is running old code
      const is404 = err.response?.status === 404;
      setConnectionHealth(prev => ({
        ...prev,
        status: is404 ? 'degraded' : 'error',
        message: is404
          ? 'Old backend detected — field backup active'
          : 'Cannot reach backend',
        lastVerifiedAt: new Date().toISOString(),
        missingFields: is404 ? ['zone_grp', 'channel_name', 'rx_freq', 'tx_tone', 'remarks'] : prev.missingFields
      }));
    }
  }, []);

  // Run backend verification on mount and every 60s
  useEffect(() => {
    verifyBackendHealth();
    const interval = setInterval(verifyBackendHealth, 60000);
    return () => clearInterval(interval);
  }, [verifyBackendHealth]);

  // Normalize a frequency object to ensure all 14 fields exist, then decode any backup
  const normalizeFrequency = (freq) => decodeBackupFields({
    ...getDefaultFrequency(),
    ...freq
  });

  // Load frequencies from backend
  const loadFrequencies = useCallback(async (silent = false) => {
    if (!currentExercise?.id) {
      console.info('[CommsPlan] loadFrequencies: No exercise ID, skipping');
      return;
    }
    // Skip silent (polling) loads while a save is in progress to prevent stale data overwrite
    if (silent && savingRef.current) return;
    
    try {
      if (!silent) setLoading(true);
      const url = `${API_BASE_URL}/api/ics-205`;
      console.info(`[CommsPlan] loadFrequencies: GET ${url}?exercise_id=${currentExercise.id} (silent=${silent})`);
      
      const response = await axios.get(url, {
        params: { exercise_id: currentExercise.id }
      });
      
      console.info('[CommsPlan] loadFrequencies response:', {
        hasData: !!response.data,
        hasFrequencies: !!(response.data && response.data.frequencies),
        frequencyCount: response.data?.frequencies?.length || 0,
        updatedAt: response.data?.updated_at,
        lastServerUpdate
      });
      
      if (response.data && response.data.frequencies) {
        // Normalize all server frequencies to ensure all 14 fields exist
        const serverFrequencies = response.data.frequencies.map(normalizeFrequency);
        const serverUpdateTime = response.data.updated_at;
        
        // Log sample data from the LAST frequency (most recently added)
        if (serverFrequencies.length > 0) {
          const last = serverFrequencies[serverFrequencies.length - 1];
          const populatedFields = Object.entries(last).filter(([k, v]) => v && k !== 'id' && k !== 'timestamp' && k !== 'updatedAt');
          console.info(`[CommsPlan] Last frequency (id=${last.id}): ${populatedFields.length}/14 fields populated`, 
            Object.fromEntries(populatedFields.slice(0, 8)));
        }
        
        // Check if server has newer data
        if (lastServerUpdate && serverUpdateTime !== lastServerUpdate) {
          console.info('[CommsPlan] MERGE branch: server has newer data');
          // Merge server data with local changes
          setIcs205Frequencies(prevFreqs => {
            // Get IDs of locally modified frequencies
            const localIds = localChangesRef.current;
            const deletedIds = deletedIdsRef.current;
            
            // Keep local modifications, add new server entries
            const merged = [...prevFreqs];
            
            serverFrequencies.forEach(serverFreq => {
              // Skip if this frequency was locally deleted
              if (deletedIds.has(serverFreq.id)) {
                return;
              }
              
              const existingIndex = merged.findIndex(f => f.id === serverFreq.id);
              if (existingIndex === -1) {
                // New frequency from server - add it
                merged.push(serverFreq);
                if (!silent) {
                  toast.info(`New frequency added by another user: ${serverFreq.frequency}`);
                }
              } else if (!localIds.has(serverFreq.id)) {
                // Existing frequency not locally modified - merge server data with local
                // Preserve local field values when server fields are missing/empty
                const localFreq = merged[existingIndex];
                merged[existingIndex] = smartMergeFreq(localFreq, serverFreq);
              }
            });
            
            // Check for deletions on server (but keep locally deleted ones out)
            const serverIds = new Set(serverFrequencies.map(f => f.id));
            const toRemove = merged.filter(f => !serverIds.has(f.id) && !localIds.has(f.id) && !deletedIds.has(f.id));
            if (toRemove.length > 0 && !silent) {
              toast.info(`${toRemove.length} frequency(ies) removed by another user`);
            }
            
            return merged.filter(f => (serverIds.has(f.id) || localIds.has(f.id)) && !deletedIds.has(f.id));
          });
        } else if (!lastServerUpdate) {
          // Initial load - but don't overwrite if user has unsaved local changes
          if (localChangesRef.current.size === 0 && !hasLocalEditsRef.current) {
            const deletedIds = deletedIdsRef.current;
            const loaded = serverFrequencies.filter(f => !deletedIds.has(f.id));
            console.info(`[CommsPlan] Initial load: Setting ${loaded.length} frequencies`);
            if (loaded.length > 0) {
              const sample = loaded[0];
              console.info('[CommsPlan] Sample frequency fields:', Object.keys(sample).sort());
              console.info('[CommsPlan] Sample frequency values:', {
                id: sample.id, zone_grp: sample.zone_grp, channel: sample.channel,
                type: sample.type, channel_name: sample.channel_name, frequency: sample.frequency
              });
            }
            setIcs205Frequencies(loaded);
          } else {
            console.info('[CommsPlan] Initial load SKIPPED — user has local edits (localChanges:', localChangesRef.current.size, ', hasLocalEdits:', hasLocalEditsRef.current, ')');
          }
          initialLoadCompleteRef.current = true;
        } else {
          console.info(`[CommsPlan] SKIP: timestamps match (${serverUpdateTime}), no state update needed`);
          // Server confirmed to be in sync — safe to clear local change tracking
          localChangesRef.current = new Set();
          deletedIdsRef.current = new Set();
        }
        
        setLastServerUpdate(serverUpdateTime);
        setLastSaved(serverUpdateTime);
      } else {
        console.info('[CommsPlan] loadFrequencies: No ICS 205 data for this exercise (response.data:', response.data, ')');
      }
    } catch (error) {
      console.error('[CommsPlan] loadFrequencies FAILED:', error.response?.status, error.response?.data || error.message);
      if (error.response?.status !== 404 && !silent) {
        console.error('Error loading ICS 205:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentExercise?.id, lastServerUpdate]);

  // Initial load and when exercise changes
  // Only reset when exercise genuinely changes (valid ID), not when it flickers null/undefined
  // due to transient API errors (e.g., 520 on exercise-builder)
  const prevExerciseIdRef = useRef(null);
  useEffect(() => {
    if (!currentExercise?.id) return; // Don't react to null/undefined exercise
    if (prevExerciseIdRef.current === currentExercise.id) return; // Same exercise, skip
    prevExerciseIdRef.current = currentExercise.id;
    
    hasLocalEditsRef.current = false;
    initialLoadCompleteRef.current = false;
    loadFrequencies();
    localChangesRef.current = new Set();
    setHasUnsavedChanges(false);
  }, [currentExercise?.id]);

  // Diagnostic: trace every ics205Frequencies state change
  useEffect(() => {
    if (ics205Frequencies.length > 0) {
      const last = ics205Frequencies[ics205Frequencies.length - 1];
      console.info(`[CommsPlan] STATE CHANGED: ${ics205Frequencies.length} freqs. Last entry:`, {
        id: last.id?.substring(0, 12),
        zone_grp: last.zone_grp || '(empty)',
        channel_name: last.channel_name || '(empty)',
        rx_freq: last.rx_freq || '(empty)',
        tx_tone: last.tx_tone || '(empty)',
        remarks: last.remarks || '(empty)',
        frequency: last.frequency || '(empty)',
        channel: last.channel || '(empty)',
      });
    } else {
      console.info('[CommsPlan] STATE CHANGED: 0 freqs (empty)');
    }
  }, [ics205Frequencies]);


  // Set up polling for real-time updates (every 15 seconds)
  useEffect(() => {
    if (currentExercise?.id) {
      pollingIntervalRef.current = setInterval(() => {
        loadFrequencies(true); // Silent refresh
      }, 15000);
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [currentExercise?.id, loadFrequencies]);

  // Manual refresh function
  const refreshFrequencies = async () => {
    localChangesRef.current = new Set(); // Clear local changes tracking
    setHasUnsavedChanges(false);
    await loadFrequencies(false);
    toast.success('Frequencies refreshed from server');
  };

  // Auto-save: silently persists the given frequencies array to the backend.
  // Debounced by 800ms to batch rapid successive calls (e.g., quick add+delete).
  // Stores pending data in a ref so it can be flushed on unmount.
  const pendingSaveRef = useRef(null);

  const executeSave = useCallback(async (frequenciesToSave) => {
    if (!currentExercise?.id) return;
    try {
      setSaveStatus('saving');

      // Encode backup fields into description before sending to backend
      const encodedFreqs = frequenciesToSave.map(encodeBackupFields);
      const payload = {
        exercise_name: currentExercise.exercise_name || '',
        frequencies: encodedFreqs
      };

      console.info(`[CommsPlan] autoSave: PUT /api/ics-205/${currentExercise.id} with ${encodedFreqs.length} frequencies`);
      if (frequenciesToSave.length > 0) {
        const last = frequenciesToSave[frequenciesToSave.length - 1];
        const populated = Object.entries(last).filter(([k, v]) => v && k !== 'id' && k !== 'timestamp' && k !== 'updatedAt');
        console.info(`[CommsPlan] autoSave sending last freq (id=${last.id}): ${populated.length} populated fields`, 
          Object.fromEntries(populated.slice(0, 8)));
      }
      
      const saveResponse = await axios.put(
        `${API_BASE_URL}/api/ics-205/${currentExercise.id}`,
        payload
      );

      console.info('[CommsPlan] autoSave SUCCESS:', {
        savedCount: saveResponse.data?.frequencies?.length,
        updatedAt: saveResponse.data?.updated_at
      });

      // Merge server response with local data, decoding any backup fields
      if (saveResponse.data?.frequencies?.length > 0) {
        const serverFreqs = saveResponse.data.frequencies.map(normalizeFrequency);
        const localFreqs = frequenciesToSave;
        const mergedFreqs = serverFreqs.map(sf => {
          const localMatch = localFreqs.find(lf => lf.id === sf.id);
          return localMatch ? smartMergeFreq(localMatch, sf) : sf;
        });
        setIcs205Frequencies(mergedFreqs);
        console.info('[CommsPlan] Post-save state merged: preserved local values over server gaps');
      }

      // Update timestamps to prevent merge conflicts on next poll
      const serverTimestamp = saveResponse.data?.updated_at || new Date().toISOString();
      setLastSaved(serverTimestamp);
      setLastServerUpdate(serverTimestamp);
      deletedIdsRef.current = new Set();
      setHasUnsavedChanges(false);
      setSaveStatus('saved');
      pendingSaveRef.current = null;

      // Post-save verification: read back and check if critical fields survived
      // Check ALL frequencies, not just the last one — catch both edits and new entries
      try {
        const verifyResp = await axios.get(`${API_BASE_URL}/api/ics-205`, {
          params: { exercise_id: currentExercise.id }
        });
        if (verifyResp.data?.frequencies?.length > 0) {
          const critical = ['zone_grp', 'channel_name', 'rx_freq', 'tx_tone', 'remarks'];
          const serverFreqs = verifyResp.data.frequencies;
          // Check each frequency: did any non-empty local value get lost on the server?
          const allLost = new Set();
          for (const localFreq of frequenciesToSave) {
            const serverFreq = serverFreqs.find(sf => sf.id === localFreq.id);
            if (!serverFreq) continue;
            // Decode the server freq to see if backup restored the values
            const decoded = decodeBackupFields(serverFreq);
            for (const f of critical) {
              if (localFreq[f] && !decoded[f]) {
                allLost.add(f);
              }
            }
          }
          const lost = Array.from(allLost);
          if (lost.length > 0) {
            console.warn('[CommsPlan] POST-SAVE VERIFY: Backend dropped fields:', lost);
            setConnectionHealth(prev => ({
              ...prev,
              status: 'degraded',
              message: `Save verified but fields lost: ${lost.join(', ')}`,
              lastVerifiedAt: new Date().toISOString(),
              missingFields: lost
            }));
          } else {
            setConnectionHealth(prev => ({
              ...prev,
              status: 'healthy',
              message: 'All fields saving correctly',
              lastVerifiedAt: new Date().toISOString(),
              missingFields: []
            }));
          }
        }
      } catch (verifyErr) {
        console.warn('[CommsPlan] Post-save verify failed:', verifyErr.message);
      }
    } catch (error) {
      console.error('[CommsPlan] autoSave FAILED:', error.response?.status, error.response?.data || error.message);
      setSaveStatus('error');
      toast.error('Failed to auto-save. Please try refreshing.');
    } finally {
      // Unblock polling after save completes (success or failure)
      savingRef.current = false;
    }
  }, [currentExercise?.id, currentExercise?.exercise_name]);

  const autoSave = useCallback((frequenciesToSave) => {
    if (!currentExercise?.id) return;

    // Block polling immediately to prevent stale data overwriting local changes
    savingRef.current = true;

    // Store data for flush-on-unmount
    pendingSaveRef.current = { exerciseId: currentExercise.id, exerciseName: currentExercise.exercise_name || '', frequencies: frequenciesToSave };

    // Clear any pending debounce
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      pendingSaveRef.current = null; // Clear immediately so unmount flush doesn't duplicate
      executeSave(frequenciesToSave);
    }, 800);
  }, [currentExercise?.id, currentExercise?.exercise_name, executeSave]);

  // On unmount: flush any pending save immediately instead of discarding it
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      // If there's pending unsaved data, fire it with keepalive so it survives unmount
      if (pendingSaveRef.current) {
        const { exerciseId, exerciseName, frequencies } = pendingSaveRef.current;
        // Encode backup fields before flushing
        const encodedFreqs = frequencies.map(encodeBackupFields);
        const payload = JSON.stringify({ exercise_name: exerciseName, frequencies: encodedFreqs });
        const url = `${API_BASE_URL}/api/ics-205/${exerciseId}`;
        try {
          fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true  // ensures request completes even after page unload
          });
          console.info('[CommsPlan] Flushed pending save on unmount via keepalive fetch');
        } catch (e) {
          console.error('[CommsPlan] Failed to flush pending save on unmount:', e);
        }
        pendingSaveRef.current = null;
      }
    };
  }, []);

  // Save frequencies to backend with proper concurrent edit support
  const saveFrequencies = async () => {
    if (!currentExercise?.id) {
      toast.error('No exercise selected');
      return;
    }
    
    try {
      setLoading(true);
      
      // Get the latest server state first
      let serverFrequencies = [];
      let serverData = null;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/ics-205`, {
          params: { exercise_id: currentExercise.id }
        });
        if (response.data && response.data.frequencies) {
          serverFrequencies = response.data.frequencies;
          serverData = response.data;
        }
      } catch (e) {
        console.warn('No existing comms plan data on server:', e.message || e);
      }
      
      // Build a map of server frequencies by ID
      const serverFreqMap = new Map(serverFrequencies.map(f => [f.id, f]));
      
      // Identify what needs to be done:
      // 1. New local frequencies (not on server) - need to be added
      // 2. Modified local frequencies - need to be updated
      // 3. Deleted local frequencies - need to be removed from final list
      // 4. Server frequencies not locally modified - keep as-is
      
      const finalFrequencies = [];
      const processedServerIds = new Set();
      
      // Process local frequencies
      for (const localFreq of ics205Frequencies) {
        // Skip deleted frequencies
        if (deletedIdsRef.current.has(localFreq.id)) {
          continue;
        }
        
        if (localChangesRef.current.has(localFreq.id)) {
          // This is a locally modified/added frequency - use local version
          finalFrequencies.push(localFreq);
        } else if (serverFreqMap.has(localFreq.id)) {
          // Not locally modified - merge server data but preserve local fields
          const serverFreq = serverFreqMap.get(localFreq.id);
          finalFrequencies.push(smartMergeFreq(localFreq, serverFreq));
        } else {
          // Local frequency not on server and not marked as local change - keep it
          finalFrequencies.push(localFreq);
        }
        processedServerIds.add(localFreq.id);
      }
      
      // Add any server frequencies we don't have locally (added by other users)
      for (const serverFreq of serverFrequencies) {
        if (!processedServerIds.has(serverFreq.id) && !deletedIdsRef.current.has(serverFreq.id)) {
          finalFrequencies.push(serverFreq);
        }
      }
      
      const payload = {
        exercise_name: currentExercise.exercise_name || '',
        frequencies: finalFrequencies.map(encodeBackupFields)
      };
      
      const saveResponse = await axios.put(`${API_BASE_URL}/api/ics-205/${currentExercise.id}`, payload);
      
      // Update local state with saved data (decoded) and use SERVER timestamp
      const serverTimestamp = saveResponse.data?.updated_at || new Date().toISOString();
      const savedFreqs = (saveResponse.data?.frequencies || finalFrequencies).map(normalizeFrequency);
      setIcs205Frequencies(savedFreqs);
      setLastSaved(serverTimestamp);
      setLastServerUpdate(serverTimestamp);
      localChangesRef.current = new Set(); // Clear local changes tracking
      deletedIdsRef.current = new Set(); // Clear deleted IDs after successful save
      setHasUnsavedChanges(false);
      
      toast.success(`Communications Plan saved (${finalFrequencies.length} frequencies)`);
    } catch (error) {
      console.error('Error saving ICS 205:', error);
      toast.error('Failed to save Communications Plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addIcs205Frequency = () => {
    // Read from ref to guarantee latest form values (prevents stale closure edge case)
    const freqData = { ...currentFreqRef.current };

    // Mark that user has made local edits — prevents stale initial load from overwriting
    hasLocalEditsRef.current = true;

    // Diagnostic: log all 14 field values at moment of add/update
    console.info('[CommsPlan] addIcs205Frequency — freqData snapshot:', {
      zone_grp: freqData.zone_grp, channel_name: freqData.channel_name,
      rx_freq: freqData.rx_freq, tx_tone: freqData.tx_tone, remarks: freqData.remarks,
      frequency: freqData.frequency, channel: freqData.channel, type: freqData.type,
      description: freqData.description, tone: freqData.tone, offset: freqData.offset,
      radio_type: freqData.radio_type, primary_backup: freqData.primary_backup,
      modulation_type: freqData.modulation_type
    });

    if (editingIcs205Frequency) {
      const updated = ics205Frequencies.map(f => 
        f.id === editingIcs205Frequency.id 
          ? { ...freqData, id: f.id, timestamp: f.timestamp, updatedAt: new Date().toISOString() }
          : f
      );
      setIcs205Frequencies(updated);
      autoSave(updated);
      localChangesRef.current.add(editingIcs205Frequency.id);
      setEditingIcs205Frequency(null);
      toast.success('Frequency updated and saved');
    } else {
      const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newFreq = {
        ...freqData,
        id: newId,
        timestamp: new Date().toISOString()
      };
      // Diagnostic: verify newFreq has all fields before setting state
      console.info('[CommsPlan] newFreq created:', {
        id: newFreq.id, zone_grp: newFreq.zone_grp, channel_name: newFreq.channel_name,
        rx_freq: newFreq.rx_freq, tx_tone: newFreq.tx_tone, remarks: newFreq.remarks,
        total_keys: Object.keys(newFreq).length
      });
      const updated = [...ics205Frequencies, newFreq];
      console.info('[CommsPlan] Setting state with', updated.length, 'freqs. Last freq zone_grp:', updated[updated.length-1].zone_grp);
      setIcs205Frequencies(updated);
      autoSave(updated);
      localChangesRef.current.add(newId);
      toast.success('Frequency added and saved');
    }
    
    setCurrentIcs205Frequency(getDefaultFrequency());
  };

  const editIcs205Frequency = (freq) => {
    setCurrentIcs205Frequency({
      frequency: freq.frequency || '',
      type: freq.type || '',
      description: freq.description || '',
      channel: freq.channel || '',
      tone: freq.tone || '',
      offset: freq.offset || '',
      radio_type: freq.radio_type || '',
      primary_backup: freq.primary_backup || 'Primary',
      modulation_type: freq.modulation_type || '',
      zone_grp: freq.zone_grp || '',
      channel_name: freq.channel_name || '',
      rx_freq: freq.rx_freq || '',
      tx_tone: freq.tx_tone || '',
      remarks: freq.remarks || ''
    });
    setEditingIcs205Frequency(freq);
  };

  const cancelEditIcs205Frequency = () => {
    setCurrentIcs205Frequency(getDefaultFrequency());
    setEditingIcs205Frequency(null);
  };

  const deleteIcs205Frequency = (freqId) => {
    if (window.confirm('Are you sure you want to delete this frequency?')) {
      hasLocalEditsRef.current = true;
      const updated = ics205Frequencies.filter(f => f.id !== freqId);
      setIcs205Frequencies(updated);
      autoSave(updated);
      deletedIdsRef.current.add(freqId);
      toast.success('Frequency deleted and saved');
    }
  };

  // Print function for ICS 205 Communications Plan
  const printIcs205Combined = () => {
    if (ics205Frequencies.length === 0) {
      toast.error('No frequencies available to print.');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>ICS 205 - Communications Plan</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #f97316; padding-bottom: 15px; }
          .header h1 { margin: 0 0 10px 0; font-size: 24px; color: #f97316; }
          .header h2 { margin: 0; font-size: 18px; color: #666; }
          .frequency-entry { border: 1px solid #ddd; margin-bottom: 15px; page-break-inside: avoid; }
          .entry-row { display: flex; border-bottom: 1px solid #eee; }
          .entry-row:last-child { border-bottom: none; }
          .entry-cell { flex: 1; padding: 8px 10px; border-right: 1px solid #eee; }
          .entry-cell:last-child { border-right: none; }
          .entry-cell.wide { flex: 2; }
          .entry-cell.full-width { flex: 100%; }
          .cell-label { font-size: 10px; color: #f97316; font-weight: bold; text-transform: uppercase; margin-bottom: 3px; }
          .cell-value { font-size: 12px; color: #333; }
          .cell-value.mono { font-family: monospace; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; }
          .badge-primary { background: #dcfce7; color: #166534; }
          .badge-backup { background: #fef9c3; color: #854d0e; }
          .badge-contingency { background: #dbeafe; color: #1e40af; }
          .entry-header { background: #f97316; color: white; padding: 8px 10px; font-weight: bold; font-size: 12px; }
          .footer { margin-top: 30px; border-top: 2px solid #ddd; padding-top: 15px; font-size: 11px; color: #666; text-align: center; }
          @media print { 
            body { margin: 0; padding: 15px; } 
            .frequency-entry { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ICS 205 - INCIDENT RADIO COMMUNICATIONS PLAN</h1>
          <h2>${currentExercise?.exercise_name || 'Exercise'}</h2>
          <p>Printed: ${new Date().toLocaleString()}</p>
        </div>
        
        ${ics205Frequencies.map((freq, index) => `
          <div class="frequency-entry">
            <div class="entry-header">Frequency Assignment #${index + 1}</div>
            
            <!-- Row 1: Zone Grp, Ch #, Function, Channel Name, Assignment -->
            <div class="entry-row">
              <div class="entry-cell">
                <div class="cell-label">Zone Grp</div>
                <div class="cell-value">${freq.zone_grp || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">Ch #</div>
                <div class="cell-value">${freq.channel || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">Function</div>
                <div class="cell-value">${freq.type || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">Channel Name</div>
                <div class="cell-value">${freq.channel_name || 'N/A'}</div>
              </div>
              <div class="entry-cell wide">
                <div class="cell-label">Assignment</div>
                <div class="cell-value">${freq.description || 'N/A'}</div>
              </div>
            </div>
            
            <!-- Row 2: RX Freq, RX Tone/NAC, TX Freq, TX Tone/NAC, Radio Type, Offset, P/B, Modulation -->
            <div class="entry-row">
              <div class="entry-cell">
                <div class="cell-label">RX Freq</div>
                <div class="cell-value mono">${freq.rx_freq || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">RX Tone/NAC</div>
                <div class="cell-value">${freq.tone || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">TX Freq</div>
                <div class="cell-value mono">${freq.frequency || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">TX Tone/NAC</div>
                <div class="cell-value">${freq.tx_tone || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">Radio Type</div>
                <div class="cell-value">${freq.radio_type || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">Offset</div>
                <div class="cell-value">${freq.offset || 'N/A'}</div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">P/B</div>
                <div class="cell-value">
                  <span class="badge ${({'Primary':'badge-primary','Backup':'badge-backup','Contingency':'badge-contingency'}[freq.primary_backup] || '')}">${freq.primary_backup || 'N/A'}</span>
                </div>
              </div>
              <div class="entry-cell">
                <div class="cell-label">Modulation</div>
                <div class="cell-value">${freq.modulation_type || 'N/A'}</div>
              </div>
            </div>
            
            <!-- Row 3: Remarks -->
            <div class="entry-row">
              <div class="entry-cell full-width">
                <div class="cell-label">Remarks</div>
                <div class="cell-value">${freq.remarks || 'N/A'}</div>
              </div>
            </div>
          </div>
        `).join('')}
        
        <div class="footer">
          <p><strong>ICS 205 - Incident Radio Communications Plan</strong></p>
          <p>Total Frequencies: ${ics205Frequencies.length}</p>
          <p>Powered by EXRSIM - Emergency Response Simulation Platform</p>
        </div>
      </body>
      </html>
    `;
    
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  return {
    // State
    ics205Frequencies,
    setIcs205Frequencies,
    currentIcs205Frequency,
    setCurrentIcs205Frequency,
    editingIcs205Frequency,
    setEditingIcs205Frequency,
    loading,
    lastSaved,
    hasUnsavedChanges,
    saveStatus,
    connectionHealth,
    // Functions
    addIcs205Frequency,
    editIcs205Frequency,
    cancelEditIcs205Frequency,
    deleteIcs205Frequency,
    printIcs205Combined,
    saveFrequencies,
    loadFrequencies,
    refreshFrequencies,
    getDefaultFrequency,
    verifyBackendHealth
  };
};

export default useCommsPlan;
